// useReportGenerator.js

import { useCallback } from 'react';
import { generateSessionReport } from '../utils/reports/ames/generateSessionReport';
import { generateSessionReportPDF } from '../utils/reports/ames/pdfReportGenerator';
import { generateSessionReportXLSX, getXLSXReportFilename } from '../utils/reports/ames/xlsxReportGenerator';
import { generateAmesLegacyReport } from '../utils/reports/ames/amesLegacyReport';
import {
  generateSAMFixedInterval,
  getSAMFixedIntervalFilename,
  generateSAMDefectsShort,
  getSAMDefectsShortFilename,
  generateSAMDefectsLong,
  getSAMDefectsLongFilename,
  generateRQFixedInterval,
  getRQFixedIntervalFilename,
} from '../utils/reports/proval';

// ============================================
// HELPERS
// ============================================

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getSessionName(session) {
  return session?.name || session?.filename || 'Session';
}

// ============================================
// AMES REPORT GENERATORS
// ============================================

async function generateAmesExport(options) {
  const { amesFormat, session, project, projectConfig, segments, events } = options;
  const sessionName = getSessionName(session);
  const reportData = { session, project, projectConfig, segments, events };

  switch (amesFormat) {
    case 'pdf': {
      const blob = await generateSessionReportPDF(reportData);
      return { blob, filename: `${sessionName}_Report.pdf` };
    }

    case 'docx': {
      const blob = await generateSessionReport(reportData);
      return { blob, filename: `${sessionName}_Report.docx` };
    }

    case 'xlsx': {
      const blob = await generateSessionReportXLSX(reportData);
      return { blob, filename: getXLSXReportFilename(session) };
    }

    case 'ames-legacy': {
      const content = generateAmesLegacyReport(reportData);
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      return { blob, filename: `${sessionName}.txt` };
    }

    default:
      throw new Error(`Unknown Ames format: ${amesFormat}`);
  }
}

// ============================================
// PROVAL EXPORT GENERATORS
// ============================================

const PROVAL_GENERATORS = {
  'sam-fixed': {
    generate: (segments, events, opts) => generateSAMFixedInterval(segments, opts),
    filename: (session, format) => getSAMFixedIntervalFilename(session, format),
  },
  'sam-defects-short': {
    generate: (segments, events, opts) => generateSAMDefectsShort(events, segments, opts),
    filename: (session, format) => getSAMDefectsShortFilename(session, format),
  },
  'sam-defects-long': {
    generate: (segments, events, opts) => generateSAMDefectsLong(segments, opts),
    filename: (session, format) => getSAMDefectsLongFilename(session, format),
  },
  'rq-fixed': {
    generate: (segments, events, opts) => generateRQFixedInterval(segments, opts),
    filename: (session, format) => getRQFixedIntervalFilename(session, format),
  },
};

function generateProvalExport(exportConfig, session, segments, events, projectConfig) {
  const { id, format, threshold } = exportConfig;
  const generator = PROVAL_GENERATORS[id];

  if (!generator) {
    throw new Error(`Unknown ProVAL export type: ${id}`);
  }

  const opts = {
    format,
    threshold: threshold || projectConfig?.iri_threshold_type_a || 160,
  };

  const content = generator.generate(segments, events, opts);
  const filename = generator.filename(session, format);
  const mimeType = format === 'csv' ? 'text/csv;charset=utf-8' : 'text/plain;charset=utf-8';
  const blob = new Blob([content], { type: mimeType });

  return { blob, filename };
}

// ============================================
// HOOK
// ============================================

export const useReportGenerator = () => {
  const generate = useCallback(async (options) => {
    const {
      exportType,
      amesFormat,
      provalExports,
      session,
      project,
      projectConfig,
      segments,
      events,
    } = options;

    const results = {
      files: [],
      errors: [],
    };

    // Generate Ames Report
    if (exportType === 'ames') {
      try {
        const { blob, filename } = await generateAmesExport({
          amesFormat,
          session,
          project,
          projectConfig,
          segments,
          events,
        });
        downloadBlob(blob, filename);
        results.files.push({ type: 'ames', filename, success: true });
      } catch (err) {
        console.error('Ames report generation failed:', err);
        results.errors.push({ type: 'ames', error: err.message });
      }
    }

    // Generate ProVAL Exports
    if (exportType === 'proval' && provalExports?.length > 0) {
      for (const exportConfig of provalExports) {
        try {
          const { blob, filename } = generateProvalExport(
            exportConfig,
            session,
            segments,
            events,
            projectConfig
          );
          downloadBlob(blob, filename);
          results.files.push({ type: 'proval', id: exportConfig.id, filename, success: true });
        } catch (err) {
          console.error(`ProVAL export ${exportConfig.id} failed:`, err);
          results.errors.push({ type: 'proval', id: exportConfig.id, error: err.message });
        }
      }
    }

    // Throw if any errors occurred
    if (results.errors.length > 0) {
      const messages = results.errors.map(e =>
        e.type === 'ames' ? `Ames Report: ${e.error}` : `${e.id}: ${e.error}`
      );
      throw new Error(messages.join('; '));
    }

    return results;
  }, []);

  return { generate };
};